import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Centralized navigation management hook
 *
 * Handles:
 * - Navigation history tracking
 * - Page state preservation
 * - Context continuity (topic, search, etc.)
 * - Smart back navigation
 */

const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get current navigation history from sessionStorage for persistence
  const getNavigationHistory = useCallback(() => {
    try {
      const stored = sessionStorage.getItem('navigationHistory');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to parse navigation history:', error);
      return [];
    }
  }, []);

  // Save navigation history to sessionStorage
  const saveNavigationHistory = useCallback((history) => {
    try {
      sessionStorage.setItem('navigationHistory', JSON.stringify(history));
    } catch (error) {
      console.error('Failed to save navigation history:', error);
    }
  }, []);

  const [navigationHistory, setNavigationHistory] = useState(() => getNavigationHistory());

  // Update navigation history and persist
  const updateHistory = useCallback((newHistory) => {
    setNavigationHistory(newHistory);
    saveNavigationHistory(newHistory);
  }, [saveNavigationHistory]);

  // Create navigation entry
  const createNavigationEntry = useCallback((type, id, context = {}, pageState = {}) => {
    const path = type === 'article' ? `/article/${id}`
                : type === 'topic' ? `/topics/${id}`
                : type === 'dashboard' ? '/dashboard'
                : type === 'search' ? `/search?q=${context.query || ''}`
                : type === 'briefing' ? '/briefing'
                : '/';

    return {
      type,
      id: id?.toString(),
      path,
      context: { ...context },
      pageState: { ...pageState },
      timestamp: Date.now()
    };
  }, []);

  // Add entry to navigation history
  const addToHistory = useCallback((entry) => {
    const newHistory = [...navigationHistory];

    // Remove any existing entry for the same path to avoid duplicates
    const existingIndex = newHistory.findIndex(item => item.path === entry.path);
    if (existingIndex !== -1) {
      newHistory.splice(existingIndex, 1);
    }

    // Add new entry to the end
    newHistory.push(entry);

    // Limit history to last 20 items for performance
    if (newHistory.length > 20) {
      newHistory.shift();
    }

    updateHistory(newHistory);
  }, [navigationHistory, updateHistory]);

  // Navigate to article with context and state preservation
  const navigateToArticle = useCallback((articleId, options = {}) => {
    const {
      context = {},
      pageState = {},
      preserveHistory = true,
      replace = false
    } = options;

    // Create current page entry before navigation if preserving history
    if (preserveHistory && !replace) {
      const currentEntry = createNavigationEntry(
        getCurrentPageType(),
        getCurrentPageId(),
        getCurrentContext(),
        getCurrentPageState()
      );
      addToHistory(currentEntry);
    }

    // Navigate to article
    const navigationState = {
      navigationId: Date.now(), // Unique ID for this navigation
      context,
      pageState
    };

    navigate(`/article/${articleId}`, {
      state: navigationState,
      replace
    });
  }, [navigate, createNavigationEntry, addToHistory]);

  // Navigate to topic with state preservation
  const navigateToTopic = useCallback((topicId, options = {}) => {
    const {
      context = {},
      pageState = {},
      preserveHistory = true,
      replace = false
    } = options;

    if (preserveHistory && !replace) {
      const currentEntry = createNavigationEntry(
        getCurrentPageType(),
        getCurrentPageId(),
        getCurrentContext(),
        getCurrentPageState()
      );
      addToHistory(currentEntry);
    }

    const navigationState = {
      navigationId: Date.now(),
      context,
      pageState
    };

    navigate(`/topics/${topicId}`, {
      state: navigationState,
      replace
    });
  }, [navigate, createNavigationEntry, addToHistory]);

  // Navigate back with intelligent history management
  const goBack = useCallback(() => {
    const history = getNavigationHistory();

    if (history.length > 0) {
      // Get the most recent history entry
      const previousEntry = history[history.length - 1];

      // Remove the entry we're going back to
      const updatedHistory = history.slice(0, -1);
      updateHistory(updatedHistory);

      // Navigate with preserved state
      const navigationState = {
        navigationId: Date.now(),
        context: previousEntry.context,
        pageState: previousEntry.pageState,
        restoredFromHistory: true
      };

      navigate(previousEntry.path, {
        state: navigationState,
        replace: true
      });
    } else {
      // No history, go to dashboard
      navigate('/dashboard');
    }
  }, [navigate, getNavigationHistory, updateHistory]);

  // Helper functions to get current page info
  const getCurrentPageType = useCallback(() => {
    const pathname = location.pathname;
    if (pathname.startsWith('/article/')) return 'article';
    if (pathname.startsWith('/topics/')) return 'topic';
    if (pathname.startsWith('/search')) return 'search';
    if (pathname === '/briefing') return 'briefing';
    if (pathname === '/dashboard' || pathname === '/') return 'dashboard';
    return 'other';
  }, [location.pathname]);

  const getCurrentPageId = useCallback(() => {
    const pathname = location.pathname;
    const match = pathname.match(/\/(article|topics)\/([^/]+)/);
    return match ? match[2] : null;
  }, [location.pathname]);

  const getCurrentContext = useCallback(() => {
    return location.state?.context || {};
  }, [location.state]);

  const getCurrentPageState = useCallback(() => {
    return location.state?.pageState || {};
  }, [location.state]);

  // Check if we can go back
  const canGoBack = useCallback(() => {
    return getNavigationHistory().length > 0;
  }, [getNavigationHistory]);

  // Get navigation context for current page
  const getNavigationContext = useCallback(() => {
    return {
      context: getCurrentContext(),
      pageState: getCurrentPageState(),
      canGoBack: canGoBack(),
      historyLength: getNavigationHistory().length,
      currentPageType: getCurrentPageType(),
      currentPageId: getCurrentPageId()
    };
  }, [getCurrentContext, getCurrentPageState, canGoBack, getNavigationHistory, getCurrentPageType, getCurrentPageId]);

  // Clear navigation history (useful for logout, etc.)
  const clearHistory = useCallback(() => {
    updateHistory([]);
  }, [updateHistory]);

  return {
    // Navigation methods
    navigateToArticle,
    navigateToTopic,
    goBack,

    // State and context
    getNavigationContext,
    canGoBack,

    // Utility methods
    clearHistory,

    // Direct access (for debugging)
    navigationHistory: getNavigationHistory()
  };
};

export default useNavigation;